import { z } from "zod";
import { ValidatorInterface } from "../validator-interface";
import { getRestUrlPrefix } from "../utils/environment";

export class EmailValidator extends ValidatorInterface {
    constructor() {
        super();
        this._cache = new Map();
        this._pendingRequests = new Map();
        this._debouceTimeout = null;
    }
    getZodSchema(baseSchema, options = {}) {
        const message = options.message || "Please enter a valid email address";
        // Ensure the base schema is a string before applying email validation
        if (baseSchema instanceof z.ZodString) {
            return baseSchema.email({ message });
        } else {
            // If the base isn't a string, this validator might be misapplied.
            // Optionally, return the base schema or throw an error.
            // eslint-disable-next-line no-console
            console.warn("EmailValidator applied to non-string schema. Ensure data-zod-type='email' or similar is used.");
            // Attempt conversion or refinement for robustness
            return z
                .string()
                .email({ message })
                .refine(s => baseSchema.safeParse(s).success, {
                    message: "Input must be a string for email validation"
                });
        }
    }

    async validateExternal(value, options = {}) {
        if (!value) return true;

        // Basic format validation is handled by zod schema, but double check here
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return false;
        }

        // Return from cache if available
        if (this._cache.has(value)) {
            return this._cache.get(value);
        }

        return new Promise(resolve => {
            this._debouceTimeout = setTimeout(async () => {
                if (this._pendingRequests.has(value)) {
                    const result = await this._pendingRequests.get(value);
                    resolve(result);
                    return;
                }
                // Perform the API call
                const validationPromise = (async () => {
                    try {
                        const restPrefix = getRestUrlPrefix();
                        const apiUrl = `${restPrefix}/freshaddress/validateEmail?email=${encodeURIComponent(value)}`;
                        const response = await fetch(apiUrl);

                        if (!response.ok) {
                            // eslint-disable-next-line no-console
                            console.error(`Email validation API error for ${value}: ${response.status} ${response.statusText}`);
                            this._cache.set(value, true);
                            return true;
                        }
                        const data = await response.json();
                        const isValid = data.valid == true && data.deliverable !== false;

                        // Cache result
                        this._cache.set(value, isValid);
                        return isValid;
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(`Email validation fetch error for ${value}:`, error);
                        this._cache.set(value, true);
                        return true;
                    } finally {
                        // Remove from pending requests once more
                        this._pendingRequests.delete(value);
                    }
                })();

                // Store the promise in pending requests
                this._pendingRequests.set(value, validationPromise);

                const result = await validationPromise;
                resolve(result);
            }, options.debounceMs || 300);
        });
    }
    getErrorMessage(field, options = {}) {
        const customMessage = options.message || field?.getAttribute("data-error-email");
        return customMessage || "Please enter a valid email address";
    }
}
